import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material'
import TranspotsErrorSnackbar from 'components/transpots-error-snackbar/transpots-error-snackbar.component'
import { useEffect, useState } from 'react'
import useSubmitLock from 'hooks/use-submit-lock'
import { FormProvider, useForm } from 'react-hook-form'
import { useSendBorderConnectTripMutation } from 'store/borderconnect/borderconnect.api'
import { getUserId } from 'utils/common'
import AceTripDetailsTab from './ace-trip-details-tab'
import { AceStepper } from './ace-trip-fields'
import {
  AceTripFormValues,
  ManifestKind,
  buildManifestTripNumber,
  defaultAceTripFormValues,
} from './ace-trip-form'
import { buildBorderConnectSendPayload } from './ace-trip-payload'
import {
  aceTripFormResolver,
  firstInvalidManifestTab,
} from './ace-trip-validation'
import AceTripShipmentsTab from './ace-trip-shipments-tab'
import { BorderConnectPrefill } from './ace-trip-autofill'

type AceTripDialogProps = {
  open: boolean
  onClose: () => void
  prefill?: BorderConnectPrefill | null
  mode?: 'create' | 'edit'
  initialValues?: AceTripFormValues | null
}

const kindButtonSx = (selected: boolean) => ({
  textTransform: 'none' as const,
  fontWeight: 700,
  borderRadius: 1,
  minWidth: 72,
  px: 2.5,
  color: selected ? '#1B7A4A' : '#2E7D57',
  bgcolor: selected ? '#D4F0DC' : 'common.white',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: selected ? '#C5E8D0' : 'grey.50',
  },
})

const AceTripDialog = ({
  open,
  onClose,
  prefill,
  mode = 'create',
  initialValues,
}: AceTripDialogProps) => {
  const [tab, setTab] = useState(0)
  const [errorMessage, setErrorMessage] = useState('')
  const userId = getUserId()
  const [sendTrip, { isLoading }] = useSendBorderConnectTripMutation()
  const { isSubmitting, wrapClick } = useSubmitLock()
  const isBusy = isLoading || isSubmitting
  const formMethods = useForm<AceTripFormValues>({
    defaultValues: defaultAceTripFormValues,
    resolver: aceTripFormResolver,
  })
  const manifestKind = formMethods.watch('manifestKind')

  const isEdit = mode === 'edit'

  useEffect(() => {
    if (!open) return
    setTab(0)
    formMethods.reset(
      isEdit
        ? (initialValues ?? defaultAceTripFormValues)
        : (prefill?.formValues ?? defaultAceTripFormValues)
    )
    setErrorMessage('')
    // Seed once on open so parent rerenders do not wipe in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleKindChange = (kind: ManifestKind) => {
    if (isEdit) return
    formMethods.setValue('manifestKind', kind)
    formMethods.setValue('usPortOfArrival', '')
    if (!prefill) return
    const nextTripNumber = buildManifestTripNumber(
      kind,
      prefill.scacCode,
      prefill.carrierCode,
      prefill.numericTripId
    )
    formMethods.setValue('tripNumber', nextTripNumber)
    const nextShipments = prefill.shipmentsByKind?.[kind] ?? []
    if (nextShipments.length > 0) {
      formMethods.setValue('shipments', nextShipments)
      return
    }
    const shipments = formMethods.getValues('shipments') ?? []
    shipments.forEach((shipment, index) => {
      if (
        shipment.shipmentType === 'PAPS' ||
        shipment.shipmentType === 'PARS' ||
        !shipment.shipmentType
      ) {
        formMethods.setValue(
          `shipments.${index}.shipmentType`,
          kind === 'ACE' ? 'PAPS' : 'PARS'
        )
      }
    })
  }

  const handleSave = formMethods.handleSubmit(
    async (values) => {
      if (!userId) {
        setErrorMessage('User session is missing. Please sign in again.')
        return
      }
      try {
        setErrorMessage('')
        await sendTrip(
          buildBorderConnectSendPayload(
            userId,
            values,
            isEdit ? 'UPDATE' : 'CREATE'
          )
        ).unwrap()
        onClose()
      } catch (err: any) {
        setErrorMessage(
          err?.data?.error?.message ||
            err?.data?.message ||
            (isEdit
              ? 'Failed to resend this eManifest to BorderConnect'
              : 'Failed to send this trip to BorderConnect')
        )
      }
    },
    (errors) => {
      setTab(firstInvalidManifestTab(errors))
      setErrorMessage('Fill in the required fields before sending.')
    }
  )

  return (
    <>
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth={false}
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: '95vw' },
          maxWidth: 1400,
          height: { xs: '100%', sm: '90vh' },
          maxHeight: 900,
          m: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={2}
          sx={{ px: 3, pt: 2.5, pb: 1.5, flexShrink: 0 }}
        >
          <Stack spacing={1.25}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 0.5,
                width: 'fit-content',
                bgcolor: 'common.white',
              }}
            >
              <Button
                disableElevation
                variant="contained"
                disabled={isBusy || isEdit}
                onClick={() => handleKindChange('ACE')}
                sx={kindButtonSx(manifestKind === 'ACE')}
              >
                ACE
              </Button>
              <Button
                disableElevation
                variant="contained"
                disabled={isBusy || isEdit}
                onClick={() => handleKindChange('ACI')}
                sx={kindButtonSx(manifestKind === 'ACI')}
              >
                ACI
              </Button>
            </Box>
            <Box>
              <Typography fontWeight={700} fontSize={22} lineHeight={1.2}>
                {manifestKind} Trip
              </Typography>
              <Typography color="text.secondary" fontSize={14}>
                {isEdit ? `Edit ${manifestKind} Trip` : `Create ${manifestKind} Trip`}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isBusy}
              sx={{
                textTransform: 'none',
                minWidth: 100,
                borderRadius: 1,
                color: 'text.secondary',
                borderColor: 'grey.400',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={wrapClick(handleSave)}
              disabled={isBusy}
              sx={{
                textTransform: 'none',
                minWidth: 100,
                borderRadius: 1,
                '&.Mui-disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
              }}
            >
              {isBusy
                ? isEdit
                  ? 'Resending...'
                  : 'Sending...'
                : isEdit
                  ? 'Resend'
                  : 'Send'}
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ px: 3, flexShrink: 0 }}>
          <AceStepper value={tab} onChange={setTab} />
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            px: 3,
            py: 2.5,
            bgcolor: '#F7F8FA',
          }}
        >
          <FormProvider {...formMethods}>
            <Box sx={{ display: tab === 0 ? 'block' : 'none' }}>
              <AceTripDetailsTab lockTripNumber={isEdit} />
            </Box>
            <Box sx={{ display: tab === 1 ? 'block' : 'none' }}>
              <AceTripShipmentsTab />
            </Box>
          </FormProvider>
        </Box>
      </DialogContent>
    </Dialog>
      <TranspotsErrorSnackbar
        message={errorMessage}
        open={errorMessage !== ''}
        handleClose={() => setErrorMessage('')}
      />
    </>
  )
}

export default AceTripDialog
